<template>
  <div class="stock-revision-sheet">
    <v-row dense class="px-4 pt-3">
      <v-col cols="12" md="4">
        <v-text-field
          v-model="search"
          :label="$t('STOCK.REVISIONS.SEARCH_PRODUCTS')"
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          hide-details
          @input="handleSearchInput"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="filters.stock_product_department_id"
          :items="departmentOptions"
          item-text="label"
          item-value="id"
          :label="$t('STOCK.REVISIONS.COLUMNS.DEPARTMENT')"
          clearable
          dense
          hide-details
          multiple
          small-chips
          deletable-chips
          @change="handleDepartmentChange"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="filters.stock_product_category_id"
          :items="filteredCategoryOptions"
          item-text="label"
          item-value="id"
          :label="$t('STOCK.REVISIONS.COLUMNS.CATEGORY')"
          clearable
          dense
          hide-details
          multiple
          small-chips
          deletable-chips
          @change="handleFilterChange"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2">
        <v-autocomplete
          v-model="filters.temperature_id"
          :items="temperatureOptions"
          item-text="label"
          item-value="id"
          :label="$t('STOCK.REVISIONS.COLUMNS.TEMPERATURE')"
          clearable
          dense
          hide-details
          @change="handleFilterChange"
        ></v-autocomplete>
      </v-col>
      <v-col v-if="!editable" cols="12" md="3">
        <v-checkbox
          v-model="filters.only_variances"
          :label="$t('STOCK.REVISIONS.ONLY_VARIANCES')"
          dense
          hide-details
          @change="handleFilterChange"
        ></v-checkbox>
      </v-col>
      <v-col v-if="!editable" cols="12" md="3">
        <v-checkbox
          v-model="filters.only_counted"
          :label="$t('STOCK.REVISIONS.ONLY_COUNTED')"
          dense
          hide-details
          @change="handleFilterChange"
        ></v-checkbox>
      </v-col>
    </v-row>

    <v-card-text v-if="editable" class="pb-0 pt-2">
      <v-alert type="info" dense text class="mb-2">
        {{ $t("STOCK.REVISIONS.SHEET_SCOPE_HINT") }}
      </v-alert>
      <StockProductLineFinder :disabled="!editable || loading || saving" @add="addProductFromFinder" />
    </v-card-text>

    <v-card-text v-if="saveError" class="pb-0">
      <v-alert type="error" dense outlined class="mb-0">{{ saveError }}</v-alert>
    </v-card-text>

    <div class="stock-document-lines-wrap mt-2">
      <v-simple-table dense class="stock-document-lines">
        <thead>
          <tr>
            <th class="stock-document-lines__sticky stock-document-lines__sticky--index">
              {{ $t("STOCK.REVISIONS.COLUMNS.PRODUCT_ID") }}
            </th>
            <th class="stock-document-lines__sticky stock-document-lines__sticky--product">
              {{ $t("STOCK.REVISIONS.COLUMNS.PRODUCT") }}
            </th>
            <th>{{ $t("STOCK.REVISIONS.COLUMNS.TAGS") }}</th>
            <th>{{ $t("STOCK.REVISIONS.COLUMNS.TEMPERATURE") }}</th>
            <th>{{ $t("STOCK.REVISIONS.COLUMNS.CATEGORY") }}</th>
            <th>{{ $t("STOCK.REVISIONS.COLUMNS.ORIGIN") }}</th>
            <th class="stock-document-lines__col-qty text-right">
              {{ $t("STOCK.REVISIONS.COLUMNS.PREVIOUS_CLOSING") }}
            </th>
            <th class="stock-document-lines__col-qty text-right">
              {{ $t("STOCK.REVISIONS.COLUMNS.MOVEMENTS_DELTA") }}
            </th>
            <th class="stock-document-lines__col-qty text-right">
              {{ $t("STOCK.REVISIONS.COLUMNS.SYSTEM_QUANTITY") }}
            </th>
            <th class="stock-document-lines__col-qty text-right">
              {{ $t("STOCK.REVISIONS.COLUMNS.COUNTED_QUANTITY") }}
            </th>
            <th class="stock-document-lines__col-qty text-right">
              {{ $t("STOCK.REVISIONS.COLUMNS.CONSUMPTION_QUANTITY") }}
            </th>
            <th class="stock-document-lines__col-qty text-right">
              {{ $t("STOCK.REVISIONS.COLUMNS.VARIANCE_QUANTITY") }}
            </th>
            <th class="stock-document-lines__col-money text-right">
              {{ $t("STOCK.REVISIONS.COLUMNS.CONSUMPTION_VALUE") }}
            </th>
            <th class="stock-document-lines__col-money text-right">
              {{ $t("STOCK.REVISIONS.COLUMNS.VARIANCE_VALUE") }}
            </th>
            <th v-if="withLots" class="text-center">
              {{ $t("STOCK.REVISIONS.COLUMNS.LOTS") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading && !rows.length">
            <td :colspan="columnCount" class="text-center grey--text py-4">
              {{ $t("STOCK.REVISIONS.LOADING_SHEET") }}
            </td>
          </tr>
          <tr v-else-if="!rows.length">
            <td :colspan="columnCount" class="text-center grey--text py-4">
              {{ $t("STOCK.REVISIONS.NO_ROWS") }}
            </td>
          </tr>
          <tr v-for="row in rows" :key="row.stock_product_id">
            <td class="stock-document-lines__sticky stock-document-lines__sticky--index">
              {{ row.stock_product_id }}
            </td>
            <td class="stock-document-lines__sticky stock-document-lines__sticky--product">
              <div class="font-weight-medium">{{ productLabel(row) }}</div>
              <div class="text-caption grey--text">
                {{ row.product && row.product.code }}
                <span v-if="productUnitCode(row)"> · {{ productUnitCode(row) }}</span>
              </div>
              <div
                v-if="row.needs_lot_assignment"
                class="text-caption orange--text text--darken-3"
              >
                {{ $t("STOCK.REVISIONS.NEEDS_LOT_ASSIGNMENT") }}
              </div>
            </td>
            <td>
              <v-chip
                v-for="tag in productTags(row)"
                :key="tag.id"
                x-small
                class="mr-1 mb-1"
                color="grey lighten-3"
              >
                {{ tag.name }}
              </v-chip>
              <span v-if="!productTags(row).length">-</span>
            </td>
            <td>{{ productTemperature(row) }}</td>
            <td>
              <div>{{ productCategoryLabel(row) }}</div>
              <div class="text-caption grey--text">{{ productDepartmentLabel(row) }}</div>
            </td>
            <td>{{ productOriginLabel(row) }}</td>
            <td class="text-right">{{ formatQuantity(row.previous_closing_quantity) }}</td>
            <td class="text-right" :class="varianceTextClass(row.movements_delta_quantity)">
              {{ formatSignedQuantity(row.movements_delta_quantity) }}
            </td>
            <td class="text-right font-weight-medium">
              {{ formatQuantity(row.system_quantity) }}
            </td>
            <td class="stock-document-lines__col-qty">
              <StockLineField
                v-if="editable"
                :value="row.counted_quantity"
                type="number"
                step="0.001"
                :hint="productUnitCode(row)"
                @focus="selectAllOnFocus"
                @input="handleCountInput(row, $event)"
              />
              <div v-else class="text-right">
                <span v-if="row.counted_quantity === null" class="grey--text">
                  {{ $t("STOCK.REVISIONS.NOT_COUNTED") }}
                </span>
                <span v-else class="font-weight-medium">
                  {{ formatQuantity(row.counted_quantity) }}
                </span>
              </div>
            </td>
            <td class="text-right">{{ formatQuantity(row.consumption_quantity) }}</td>
            <td class="text-right" :class="varianceTextClass(row.variance_quantity)">
              {{ formatSignedQuantity(row.variance_quantity) }}
            </td>
            <td class="text-right">
              {{ formatMoney(row.consumption_value, row.currency_code) }}
            </td>
            <td class="text-right" :class="varianceTextClass(row.variance_value)">
              {{ formatSignedMoney(row.variance_value, row.currency_code) }}
            </td>
            <td v-if="withLots" class="text-center">
              <v-btn
                x-small
                text
                color="primary"
                :disabled="!row.stock_revision_item_id"
                @click="$emit('open-lots', row)"
              >
                {{ $t("STOCK.REVISIONS.COLUMNS.LOTS") }}
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <div class="d-flex align-center flex-wrap px-4 py-2">
      <div class="caption grey--text text--darken-1">
        {{ rangeLabel }}
      </div>
      <v-progress-circular
        v-if="saving"
        indeterminate
        size="16"
        width="2"
        color="primary"
        class="ml-3"
      ></v-progress-circular>
      <span v-if="saving" class="caption grey--text text--darken-1 ml-2">
        {{ $t("STOCK.REVISIONS.SAVING_PAGE") }}
      </span>
      <v-spacer></v-spacer>
      <v-pagination
        v-if="pageCount > 1"
        v-model="page"
        :length="pageCount"
        :total-visible="9"
        :disabled="loading || saving"
        @input="handlePageChange"
      ></v-pagination>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";
import StockLineField from "@/views/partials/stock/StockLineField.vue";
import StockProductLineFinder from "@/views/partials/stock/StockProductLineFinder.vue";
import stockRevisionDisplayMixin from "@/views/partials/stock/revisions/stockRevisionDisplay.mixin";
import {
  GET_STOCK_REVISION_COUNT_SHEET,
  SAVE_STOCK_REVISION_COUNTS,
} from "@/store/stock/stockRevisions.module";
import { GET_STOCK_PRODUCT_CATEGORY_OPTIONS } from "@/store/stock/stockProductCategories.module";
import { GET_STOCK_PRODUCT_DEPARTMENT_OPTIONS } from "@/store/stock/stockProductDepartments.module";
import { GET_STOCK_TEMPERATURE_OPTIONS } from "@/store/stock/stockProducts.module";

const PER_PAGE = 100;

export default {
  name: "StockRevisionCountSheet",
  components: { StockLineField, StockProductLineFinder },
  mixins: [stockRevisionDisplayMixin],
  props: {
    revisionId: {
      type: [Number, String],
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    withLots: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rows: [],
      page: 1,
      total: 0,
      loading: false,
      saving: false,
      saveError: "",
      search: "",
      filters: {
        stock_product_category_id: [],
        stock_product_department_id: [],
        temperature_id: null,
        only_variances: false,
        only_counted: false,
      },
      categoryOptions: [],
      departmentOptions: [],
      temperatureOptions: [],
      dirtyProductIds: [],
    };
  },
  computed: {
    pageCount() {
      return Math.max(1, Math.ceil(this.total / PER_PAGE));
    },
    columnCount() {
      return this.withLots ? 15 : 14;
    },
    rangeLabel() {
      if (!this.total) {
        return "";
      }
      const from = (this.page - 1) * PER_PAGE + 1;
      const to = Math.min(this.page * PER_PAGE, this.total);
      return `${from}–${to} / ${this.total}`;
    },
    hasUnsavedCounts() {
      return this.dirtyProductIds.length > 0;
    },
    filteredCategoryOptions() {
      const departmentIds = this.filters.stock_product_department_id || [];
      if (!departmentIds.length) {
        return this.categoryOptions;
      }
      return this.categoryOptions.filter((category) =>
        departmentIds.includes(category.stock_product_department_id)
      );
    },
  },
  mounted() {
    this.loadFilterOptions();
    this.load();
  },
  watch: {
    revisionId(id, previousId) {
      if (!id || String(id) === String(previousId)) {
        return;
      }
      this.page = 1;
      this.search = "";
      this.filters = {
        stock_product_category_id: [],
        stock_product_department_id: [],
        temperature_id: null,
        only_variances: false,
        only_counted: false,
      };
      this.dirtyProductIds = [];
      this.load();
    },
  },
  methods: {
    queryParams() {
      const params = {
        page: this.page,
        itemsPerPage: PER_PAGE,
        filters: {},
      };

      if (this.search) {
        params.search = this.search;
      }

      Object.keys(this.filters).forEach((key) => {
        const value = this.filters[key];
        if (value === null || value === "" || value === false) {
          return;
        }
        if (Array.isArray(value) && !value.length) {
          return;
        }
        params.filters[key] = value === true ? 1 : value;
      });

      return params;
    },
    load() {
      this.loading = true;
      return this.$store
        .dispatch(GET_STOCK_REVISION_COUNT_SHEET, {
          id: this.revisionId,
          params: this.queryParams(),
        })
        .then((response) => {
          this.rows = (response.data || []).map((row) => ({ ...row }));
          this.total = response.meta ? response.meta.total : this.rows.length;
          this.dirtyProductIds = [];
        })
        .catch((error) => {
          this.$emit("error", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadFilterOptions() {
      this.$store
        .dispatch(GET_STOCK_PRODUCT_DEPARTMENT_OPTIONS, {})
        .then((response) => {
          this.departmentOptions = this.mapOptions(response.data || response);
        })
        .catch(() => {});
      this.$store
        .dispatch(GET_STOCK_PRODUCT_CATEGORY_OPTIONS, {})
        .then((response) => {
          this.categoryOptions = this.mapCategoryOptions(response.data || response);
        })
        .catch(() => {});
      this.$store
        .dispatch(GET_STOCK_TEMPERATURE_OPTIONS)
        .then((response) => {
          this.temperatureOptions = this.mapOptions(response.data || response);
        })
        .catch(() => {});
    },
    mapOptions(items) {
      return (items || []).map((item) => ({
        id: item.id,
        label: item.label || item.name || String(item.id),
      }));
    },
    mapCategoryOptions(items) {
      return (items || []).map((item) => ({
        id: item.id,
        label: item.label || item.name || String(item.id),
        stock_product_department_id: item.stock_product_department_id || null,
      }));
    },
    handleSearchInput: debounce(function () {
      this.reloadFromFirstPage();
    }, 400),
    handleDepartmentChange() {
      const allowedIds = this.filteredCategoryOptions.map((category) => category.id);
      this.filters.stock_product_category_id = (this.filters.stock_product_category_id || []).filter(
        (id) => allowedIds.includes(id)
      );
      this.handleFilterChange();
    },
    handleFilterChange() {
      this.reloadFromFirstPage();
    },
    async reloadFromFirstPage() {
      const saved = await this.flush();
      if (!saved) {
        return;
      }
      this.page = 1;
      this.load();
    },
    async handlePageChange(page) {
      const saved = await this.flush();
      if (!saved) {
        return;
      }
      this.page = page;
      this.load();
    },
    handleCountInput(row, value) {
      const normalized = value === "" || value === null || value === undefined ? null : Number(value);
      if (normalized !== null && Number.isNaN(normalized)) {
        return;
      }

      row.counted_quantity = normalized;
      row.counted_is_default = false;
      this.recomputeRow(row);

      if (!this.dirtyProductIds.includes(row.stock_product_id)) {
        this.dirtyProductIds.push(row.stock_product_id);
      }

      this.$emit("dirty-change", true);
    },
    /**
     * Inject a product that is not in the stocked/moved scope (zero balance, no history).
     */
    async addProductFromFinder(product) {
      if (!product || !product.id || !this.editable) {
        return;
      }

      const alreadyOnPage = this.rows.some((row) => row.stock_product_id === product.id);
      if (alreadyOnPage) {
        this.$root.$emit("snackbar", {
          color: "info",
          message: this.$t("STOCK.REVISIONS.PRODUCT_ALREADY_ON_SHEET"),
        });
        return;
      }

      const saved = await this.flush();
      if (!saved) {
        return;
      }

      this.saving = true;
      this.saveError = "";

      try {
        const lookup = await this.$store.dispatch(GET_STOCK_REVISION_COUNT_SHEET, {
          id: this.revisionId,
          params: {
            page: 1,
            itemsPerPage: 20,
            search: product.code || product.display_name || "",
          },
        });
        const alreadyOnSheet = (lookup.data || []).some(
          (row) => row.stock_product_id === product.id
        );

        if (!alreadyOnSheet) {
          await this.$store.dispatch(SAVE_STOCK_REVISION_COUNTS, {
            id: this.revisionId,
            counts: [{ stock_product_id: product.id, counted_quantity: 0 }],
          });
          this.$root.$emit("snackbar", {
            color: "success",
            message: this.$t("STOCK.REVISIONS.PRODUCT_ADDED"),
          });
          this.$emit("saved");
        } else {
          this.$root.$emit("snackbar", {
            color: "info",
            message: this.$t("STOCK.REVISIONS.PRODUCT_ALREADY_ON_SHEET"),
          });
        }

        // Keep the full sheet visible — do not push the product into the sheet filter.
        this.search = "";
        this.page = 1;
        await this.load();
      } catch (error) {
        this.saveError = (error && error.message) || this.$t("STOCK.REVISIONS.SAVE_FAILED");
      } finally {
        this.saving = false;
      }
    },
    /**
     * Mirrors the backend sheet formulas so the operator sees the effect of a count
     * before the page is saved.
     */
    recomputeRow(row) {
      const system = Number(row.system_quantity || 0);
      const counted = row.counted_quantity;
      const closing = counted === null ? system : Number(counted);
      const unitCost = row.unit_cost === null || row.unit_cost === undefined ? null : Number(row.unit_cost);

      row.closing_quantity = this.round(closing);
      row.variance_quantity = this.round(counted === null ? 0 : closing - system);
      row.consumption_quantity = this.round(
        Number(row.previous_closing_quantity || 0) + Number(row.inbound_quantity || 0) - closing
      );
      row.consumption_value = unitCost === null ? null : this.round(row.consumption_quantity * unitCost, 4);
      row.variance_value = unitCost === null ? null : this.round(row.variance_quantity * unitCost, 4);
    },
    round(value, precision = 4) {
      const factor = 10 ** precision;
      return Math.round(Number(value || 0) * factor) / factor;
    },
    /**
     * Persists the counts entered on the current page, including Ideal→Actual defaults
     * the operator left untouched (counted_is_default). Resolves false when the save
     * failed, so navigation can stay put instead of silently dropping input.
     */
    async flush() {
      if (!this.editable) {
        return true;
      }

      const counts = this.rows
        .filter(
          (row) =>
            this.dirtyProductIds.includes(row.stock_product_id) ||
            Boolean(row.counted_is_default)
        )
        .map((row) => ({
          stock_product_id: row.stock_product_id,
          counted_quantity: row.counted_quantity,
        }));

      if (!counts.length) {
        return true;
      }

      this.saving = true;
      this.saveError = "";

      try {
        await this.$store.dispatch(SAVE_STOCK_REVISION_COUNTS, {
          id: this.revisionId,
          counts,
        });
        const savedIds = new Set(counts.map((count) => count.stock_product_id));
        this.rows.forEach((row) => {
          if (savedIds.has(row.stock_product_id)) {
            row.counted_is_default = false;
          }
        });
        this.dirtyProductIds = [];
        this.$emit("dirty-change", false);
        this.$emit("saved");
        return true;
      } catch (error) {
        this.saveError = (error && error.message) || this.$t("STOCK.REVISIONS.SAVE_FAILED");
        return false;
      } finally {
        this.saving = false;
      }
    },
    selectAllOnFocus(event) {
      const target = event && event.target;
      if (target && typeof target.select === "function") {
        target.select();
      }
    },
    refresh() {
      return this.load();
    },
  },
};
</script>

<style scoped>
.stock-document-lines-wrap {
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}
.stock-document-lines {
  border-collapse: separate;
  border-spacing: 0;
}
.stock-document-lines th,
.stock-document-lines td {
  vertical-align: top !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.stock-document-lines thead th {
  background: #f5f5f5;
  white-space: nowrap;
}
.stock-document-lines__sticky--index {
  position: sticky;
  left: 0;
  z-index: 2;
  width: 72px;
  min-width: 72px;
  max-width: 72px;
  background: #fff;
  text-align: center;
}
.stock-document-lines__sticky--product {
  position: sticky;
  left: 72px;
  z-index: 2;
  min-width: 260px;
  width: 260px;
  max-width: 280px;
  background: #fff;
  box-shadow: 4px 0 8px -4px rgba(0, 0, 0, 0.14);
}
.stock-document-lines thead .stock-document-lines__sticky {
  z-index: 4;
  background: #f5f5f5;
}
.stock-document-lines__col-qty {
  min-width: 112px;
}
.stock-document-lines__col-money {
  min-width: 132px;
}
.stock-document-lines :deep(.stock-line-field__input) {
  width: 100%;
}
.stock-document-lines :deep(.stock-line-field__input .v-input__slot) {
  min-height: 36px !important;
}
</style>
